import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  InvoiceMailAttachmentPreview,
  isPdfAttachment,
} from "@/components/invoiceMail/InvoiceMailAttachmentPreview";
import { ApiError } from "@/lib/apiClient";
import {
  invoiceMailStatusLabel,
  listInvoiceMailForOrder,
  type InvoiceMailCandidate,
} from "@/lib/invoiceMail";
import { fmtOrderDateTime } from "@/pages/bestellungen/orderHistoryDisplay";

function fmtAttachmentSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function CandidateBlock({ candidate }: { candidate: InvoiceMailCandidate }) {
  const attachments = (candidate.attachments_summary || []).filter((a) => a.id);
  const [previewId, setPreviewId] = useState<string | null>(
    attachments.find((a) => isPdfAttachment(a))?.id || attachments[0]?.id || null,
  );
  const previewAtt = attachments.find((a) => a.id === previewId) || null;
  const pending = candidate.status === "pending";

  return (
    <article className="space-y-3 rounded-ios border border-separator bg-card p-4">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={
              pending
                ? "rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange"
                : "rounded-full bg-status-green/15 px-2 py-0.5 text-xs font-semibold text-status-green"
            }
          >
            {invoiceMailStatusLabel(candidate.status)}
          </span>
          {candidate.received_at ? (
            <time className="text-xs text-label-secondary" dateTime={candidate.received_at}>
              {fmtOrderDateTime(candidate.received_at)}
            </time>
          ) : null}
        </div>
        <h3 className="text-sm font-semibold text-label">{candidate.subject || "Ohne Betreff"}</h3>
        <p className="text-xs text-label-secondary">
          {[candidate.from_name, candidate.from_email].filter(Boolean).join(" · ") || "—"}
        </p>
        {pending ? (
          <p className="text-xs text-label-secondary">
            Noch nicht auf SharePoint abgelegt – Vorschau direkt aus dem Postfach.
          </p>
        ) : null}
      </header>

      {attachments.length ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-label-secondary">Anhänge</p>
          <ul className="flex flex-wrap gap-2">
            {attachments.map((a) => {
              const id = a.id || "";
              const active = id === previewId;
              return (
                <li key={id}>
                  <button
                    type="button"
                    className={
                      active
                        ? "rounded-lg border border-accent bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent"
                        : "rounded-lg border border-separator bg-fill-tertiary px-2.5 py-1 text-xs text-label hover:border-accent/40"
                    }
                    onClick={() => setPreviewId(id)}
                  >
                    {a.name || "Anhang"}
                    {fmtAttachmentSize(a.size) ? ` (${fmtAttachmentSize(a.size)})` : ""}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-label-tertiary">Keine Anhänge.</p>
      )}

      {previewAtt?.id ? (
        <InvoiceMailAttachmentPreview candidateId={candidate.id} attachment={previewAtt} />
      ) : null}
    </article>
  );
}

export function InvoiceForOrderDialog({
  open,
  orderId,
  bestellnummern,
  onClose,
  zIndexClass = "z-[120]",
}: {
  open: boolean;
  orderId: number | null;
  bestellnummern?: string | null;
  onClose: () => void;
  zIndexClass?: string;
}) {
  const [items, setItems] = useState<InvoiceMailCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !orderId) {
      setItems([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    void listInvoiceMailForOrder(orderId)
      .then((list) => setItems(list))
      .catch((err) => {
        setItems([]);
        setError(err instanceof ApiError ? err.message : "Rechnungen konnten nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [open, orderId]);

  if (!open) return null;

  const titleTk = (bestellnummern || "").trim();

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span>Rechnung{titleTk ? ` · ${titleTk}` : ""}</span>
        </span>
      }
      size="2xl"
      zIndexClass={zIndexClass}
      fullScreenMobile
      bodyClassName="space-y-4"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to="/bestellwesen/rechnungs-postfach"
            className="text-sm font-medium text-accent hover:underline"
          >
            Rechnungs-Postfach öffnen
          </Link>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : null}
      {error ? (
        <p className="rounded-ios border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {error}
        </p>
      ) : null}
      {!loading && !error && !items.length ? (
        <p className="text-sm text-label-secondary">Keine zugeordneten Rechnungs-Mails gefunden.</p>
      ) : null}
      {!loading && !error
        ? items.map((c) => <CandidateBlock key={c.id} candidate={c} />)
        : null}
    </Dialog>
  );
}
